/**
 * LendingReportDrawer - Main drawer shell for the Lending Report
 *
 * Fetches report data + decision, renders metrics bar, tab switcher,
 * tab content, and decision bar/banner.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Loader,
  TabSwitcher,
} from 'equal-ds-ui';
import { FileBarChart, Copy, Check, PanelRightClose } from 'lucide-react';
import { lendingReportAPI } from '../../../../services/lending-report-api.service';
import type {
  LendingReportDrawerProps,
  LendingReportData,
  LendingDecision,
  LendingReportTab,
} from '../../../../types/lending-report.types';
import { SummaryMetricsBar } from './SummaryMetricsBar';
import { SummaryTab } from './SummaryTab';
import { IncomeTab } from './IncomeTab';
import { ObligationsTab } from './ObligationsTab';
import { CashflowTab } from './CashflowTab';
import { BehaviorTab } from './BehaviorTab';
import { DecisionActionBar } from './DecisionActionBar';
import { DecisionBanner } from './DecisionBanner';

const TABS: { id: LendingReportTab; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'income', label: 'Income' },
  { id: 'obligations', label: 'Obligations' },
  { id: 'cashflow', label: 'Cashflow' },
  { id: 'behavior', label: 'Behavior' },
];

import { formatPeriod } from '../../../../utils/lending-report-utils';

export const LendingReportDrawer: React.FC<LendingReportDrawerProps> = ({
  isOpen,
  onClose,
  consentId,
  consentHandle: _consentHandle,
}) => {
  const [report, setReport] = useState<LendingReportData | null>(null);
  const [decision, setDecision] = useState<LendingDecision | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LendingReportTab>('summary');
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state when drawer closes to prevent stale data on reopen
  useEffect(() => {
    if (!isOpen) {
      setReport(null);
      setDecision(null);
      setError(null);
      setActiveTab('summary');
      setCopied(false);
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    }
  }, [isOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (!consentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const reportResponse = await lendingReportAPI.getReport(consentId);
      setReport(reportResponse.data);

      try {
        const decisionResponse = await lendingReportAPI.getDecision(consentId);
        setDecision(decisionResponse.data?.decision || null);
      } catch {
        // Decision fetch failure is non-fatal; report still renders
        setDecision(null);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { errorCode?: string } }; status?: number } | null;
      const status = axiosErr?.response?.status || axiosErr?.status;
      const errorCode = axiosErr?.response?.data?.errorCode;

      let message: string;
      if (errorCode === 'NO_FI_DATA' || status === 404) {
        message = 'No financial data available for this consent. Please ensure FI data has been fetched before generating the lending report.';
      } else if (errorCode === 'EWS_CONFIG_MISSING') {
        message = 'Lending report analytics is not configured. Please contact your administrator.';
      } else if (errorCode === 'EWS_API_FAILED' || status === 502) {
        message = 'The analytics service is temporarily unavailable. Please try again later.';
      } else {
        message = err instanceof Error ? err.message : 'Failed to load lending report';
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [consentId]);

  useEffect(() => {
    if (isOpen && consentId) {
      fetchData();
    }
  }, [isOpen, consentId, fetchData]);

  const handleCopyConsentId = async () => {
    if (!consentId) return;
    try {
      await navigator.clipboard.writeText(consentId);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore clipboard errors
    }
  };

  const handleDecisionSubmitted = useCallback((newDecision: LendingDecision) => {
    setDecision(newDecision);
  }, []);

  const renderTabContent = () => {
    if (!report) return null;

    switch (activeTab) {
      case 'summary': return <SummaryTab report={report} />;
      case 'income': return <IncomeTab report={report} />;
      case 'obligations': return <ObligationsTab report={report} />;
      case 'cashflow': return <CashflowTab report={report} />;
      case 'behavior': return <BehaviorTab report={report} />;
      default: return null;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="65%"
        className="border-border-default bg-gray-100"
        role="dialog"
        aria-label="Lending Report"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div>
            <div className="flex items-center gap-3">
              <span style={{ fontSize: '16px', fontWeight: 700, color: '#111827', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {consentId || '-'}
              </span>
              {report?.employmentType && (
                <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 10px', borderRadius: '9999px', backgroundColor: '#ecfdf5', color: '#059669' }}>
                  {report.employmentType}
                </span>
              )}
              {consentId && (
                <button
                  onClick={handleCopyConsentId}
                  className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Copy Consent ID"
                  aria-label="Copy Consent ID"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </div>
            {report?.statementPeriodFrom && report?.statementPeriodTo && (
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                Statement Period: {formatPeriod(report.statementPeriodFrom)} &ndash; {formatPeriod(report.statementPeriodTo)}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close lending report"
          >
            <PanelRightClose className="w-5 h-5" />
          </button>
        </div>

        <DrawerContent className="flex flex-col flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader size={72} cellSize={20} />
            </div>
          ) : error ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '256px', gap: '16px', padding: '24px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileBarChart style={{ width: 24, height: 24, color: '#dc2626' }} />
              </div>
              <div style={{ fontSize: '14px', color: '#374151', textAlign: 'center', maxWidth: '400px', lineHeight: 1.6 }}>{error}</div>
              <button
                onClick={fetchData}
                style={{ fontSize: '13px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Retry
              </button>
            </div>
          ) : !report ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No report data available</div>
            </div>
          ) : (
            <>
              {/* Decision Banner (if decision exists) */}
              {decision && (
                <div className="px-6 pt-4">
                  <DecisionBanner decision={decision} />
                </div>
              )}

              {/* Summary Metrics Bar */}
              <div className="px-6 pt-4">
                <SummaryMetricsBar report={report} />
              </div>

              {/* Tabs */}
              <div className="px-6 pt-4 space-y-4 flex-1">
                <TabSwitcher
                  tabs={TABS}
                  activeTab={activeTab}
                  onTabChange={(tabId: string) => setActiveTab(tabId as LendingReportTab)}
                  variant="horizontal"
                />

                <div className="pb-6">
                  {renderTabContent()}
                </div>
              </div>

              {/* Decision Action Bar */}
              <DecisionActionBar
                consentId={consentId}
                existingDecision={decision}
                onDecisionSubmitted={handleDecisionSubmitted}
              />
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};
